import React, { FormEventHandler, useEffect } from 'react'

import { useMutation } from '@apollo/client'

import BuildConfig from '$extensionSrc/buildConfig'
import Toast from '$extensionSrc/components/Toast'
import {
  makeConfirmEmailURL,
  privacyPolicyUrl,
  tosUrl,
} from '$extensionSrc/consts/urlConsts'
import useForm from '$extensionSrc/hooks/useForm'
import { hasMinLength } from '$extensionSrc/utils/stringValidator'

import FormSubmitButton from './FormSubmitButton'
import FormTextDescription from './FormTextDescription'
import FormTextInput from './FormTextInput'
import LoginSignupLink from './LoginSignupLink'
import { unauthClient } from '../apolloClientUnauth'
import { PasswordExceptionType, UserExceptionType } from '../consts/enums'
import { signupMutation } from '../data/queryUnauth'

import './SignupForm.scss'

function getErrorToastMessage(signupError: string): string {
  if (signupError === UserExceptionType.USER_EXISTS_EXCEPTION) {
    return 'User with that email already exists'
  } else if (
    signupError === PasswordExceptionType.PASSWORD_TOO_SHORT_EXCEPTION
  ) {
    return 'Password must be at least 16 characters'
  }
  return 'Signup attempt failed'
}

export default function SignupForm({
  initialError,
  email,
}: {
  initialError: string
  email: string
}) {
  const defaultValues = { firstName: '', password: '', confirmPassword: '' }
  const requiredValidations = {
    password: (x: string) => hasMinLength(x, 16),
  }

  const {
    getFieldChangeHandler,
    getRequiredValidationMetByField,
    isValid: baseIsValid,
    values,
  } = useForm({
    defaultValues,
    requiredValidations,
  })

  // Custom password matching validation after values is available
  const passwordsMatch = values.confirmPassword === values.password
  const confirmPasswordValid =
    values.confirmPassword.length > 0 && passwordsMatch

  // Combine the base form validation with password matching validation
  const isValid = baseIsValid && confirmPasswordValid

  const [submitSignupRequest, { data: signupResponseData }] = useMutation(
    signupMutation,
    { client: unauthClient },
  )
  const signupStatusCode = signupResponseData?.signUp?.statusCode
  const signupError = signupResponseData?.signUp?.error
  const signupUserEmail = signupResponseData?.signUp?.user?.email

  const submitForm: FormEventHandler = (event) => {
    event.preventDefault()

    // Additional check to ensure passwords match before submitting
    if (!passwordsMatch) {
      return
    }

    submitSignupRequest({
      variables: {
        name: values.firstName,
        email,
        password: values.password,
      },
    })
  }

  useEffect(() => {
    if (signupStatusCode === 200 && signupUserEmail) {
      window.location.assign(makeConfirmEmailURL(signupUserEmail))
    }
  }, [signupStatusCode, signupUserEmail])

  return (
    <form className="SignupForm" onSubmit={submitForm}>
      <div className="form-header">
        <div className="form-title bold heading1">
          Create your {BuildConfig.TENANT_NAME} account
        </div>
      </div>
      <div className="form-content">
        <FormTextInput
          placeholderText="Create a password"
          name="password"
          isSensitive
          value={values.password}
          handleChange={(value) => getFieldChangeHandler('password')(value)}
          requirements={[
            {
              text: 'Enter 16 characters or more',
              isFulfilled: getRequiredValidationMetByField('password'),
            },
          ]}
        />
        <FormTextInput
          placeholderText="Confirm your password"
          name="confirmPassword"
          isSensitive
          value={values.confirmPassword}
          handleChange={(value) =>
            getFieldChangeHandler('confirmPassword')(value)
          }
          requirements={[
            {
              text: 'Passwords must match',
              isFulfilled: passwordsMatch,
            },
          ]}
        />
        <FormTextInput
          placeholderText="What name should we call you? (optional)"
          name="firstName"
          isSensitive={false}
          value={values.firstName}
          handleChange={(value) => getFieldChangeHandler('firstName')(value)}
        />
      </div>
      <FormSubmitButton isValid={isValid}>Continue</FormSubmitButton>
      {signupStatusCode !== 200 && signupError && (
        <Toast
          text={getErrorToastMessage(signupError)}
          noAutoDismiss
          showCloseIcon
          color="red"
        />
      )}
      {initialError && (
        <Toast text={initialError} noAutoDismiss showCloseIcon color="red" />
      )}
      <FormTextDescription align="center">
        By continuing, you acknowledge that you understand and agree to Block
        Party's{' '}
        <a href={tosUrl} target="_blank" rel="noreferrer">
          Terms & Conditions
        </a>{' '}
        and{' '}
        <a href={privacyPolicyUrl} target="_blank" rel="noreferrer">
          Privacy Policy
        </a>
        .
      </FormTextDescription>
      <LoginSignupLink />
    </form>
  )
}
